// src/utils/useScrollToHash.js
// Custom hook that scrolls to the URL hash anchor on mount and on hash changes.
// Used by contextual settings pages so anchor navigation from the Integrations
// page (/dashboard/settings/integrations) lands on the right section.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Matches the pt-20 (80px) on page containers — covers the fixed top nav
// rendered by Layout.js so the scrolled-to element doesn't hide behind it.
const NAV_OFFSET_PX = 80;

// ~500ms at 60fps — enough for async data loads to render the target.
const MAX_ATTEMPTS = 30;

export function useScrollToHash() {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace(/^#/, '');
    if (!hash) return;

    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(hash);
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - NAV_OFFSET_PX;
        window.scrollTo({ top, behavior: 'smooth' });
        return;
      }
      attempts += 1;
      if (attempts < MAX_ATTEMPTS) {
        requestAnimationFrame(tryScroll);
      }
      // After MAX_ATTEMPTS, silently give up (graceful failure per spec).
    };

    requestAnimationFrame(tryScroll);

    return () => { cancelled = true; };
  }, [location.hash]);
}
